import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { CalendarDays, Home, LogOut } from 'lucide-react';
import { setSessionExpiredHandler } from '../../shared/lib/axios';
import { SessionRenewalModal } from '../../shared/components/SessionRenewalModal';
import type { User as AuthUser } from '../../shared/lib/auth';
import { usePortalDoCliente } from '../../features/clientes/hooks/acessosExternos';
import { NotificationBell } from '../../shared/components/NotificationBell';

interface Props {
  children: React.ReactNode;
  user: AuthUser;
  onSignOut?: () => void;
}

const NAV_ITEMS = [
  { id: 'inicio', label: 'Início', icon: <Home className="h-4 w-4" />, path: '/portal-cliente', exact: true },
  { id: 'ferias', label: 'Férias da equipe', icon: <CalendarDays className="h-4 w-4" />, path: '/portal-cliente/ferias', exact: false },
];

/**
 * Portal do usuário externo do cliente (change `acesso-gestor-cliente`).
 * Layout próprio, como o do prestador PJ: o perfil `cliente` não vê o menu interno.
 */
export const ClientePortalLayout: React.FC<Props> = ({ children, user, onSignOut }) => {
  const location = useLocation();
  const portal = usePortalDoCliente();
  const [sessionExpired, setSessionExpired] = useState(false);

  useEffect(() => {
    setSessionExpiredHandler(() => setSessionExpired(true));
    return () => setSessionExpiredHandler(() => {});
  }, []);

  const cliente = portal.data?.client;

  return (
    <div className="flex min-h-screen flex-col bg-gray-50">
      <header className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-200 bg-white px-4 py-3 sm:px-6">
        <div>
          <span className="text-lg font-bold text-gray-900">Portal do Cliente</span>
          {cliente && <span className="block text-sm text-gray-600">{cliente.tradeName || cliente.corporateName}</span>}
        </div>
        <div className="flex items-center gap-4">
          <NotificationBell />
          <span className="text-sm text-gray-600">{user.name}</span>
          {onSignOut && (
            <button type="button" onClick={onSignOut} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700">
              <LogOut className="h-4 w-4" /> Sair
            </button>
          )}
        </div>
      </header>

      <nav className="border-b border-gray-200 bg-white px-4 sm:px-6">
        <ul className="flex gap-1 overflow-x-auto">
          {NAV_ITEMS.map((item) => {
            const active = item.exact ? location.pathname === item.path : location.pathname.startsWith(item.path);
            return (
              <li key={item.id}>
                <Link to={item.path}
                  className={`flex items-center gap-2 border-b-2 px-3 py-2 text-sm font-medium ${
                    active ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-600 hover:text-gray-900'
                  }`}>
                  {item.icon}{item.label}
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>

      <main className="flex-1 p-4 sm:p-6">{children}</main>

      <SessionRenewalModal
        isOpen={sessionExpired}
        onRenewed={() => setSessionExpired(false)}
        onSignOut={() => {
          setSessionExpired(false);
          onSignOut?.();
        }}
      />
    </div>
  );
};
